import AppModeSwitch from 'components/dumb/atomics/AppModeSwitch';
import EmptyPackagesState from 'components/dumb/atomics/EmptyPackagesState';
import SearchBar from 'components/dumb/atomics/SearchBar';
import SearchFiltersBar from 'components/dumb/atomics/SearchFiltersBar';
import TabBar from 'components/dumb/atomics/TabBar';
import { AppMode, SearchFilters } from 'helpers/constants/enums';
import { ENABLE_APP_MODE_SWITCH } from 'helpers/constants/envVars';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BusinessExperience, Experience } from 'uiModels/interfaces';
import FiltersIcon from '../../../../assets/icons/buttonIcons/FiltersIcon';
import {
  backgroundContainerClass,
  menuBarClass,
} from '../../../../helpers/constants/styling';
import { getExperienceCardType } from '../../../../helpers/functions/experience/experienceCardType';
import theme from '../../../../styles/theme/classic';
import { Option } from '../../atomics/DropdownInput';
import ExperienceCard, { ActionButton } from '../../atomics/ExperienceCard';
import IconButton from '../../atomics/IconButton';
import ExperienceSkeleton from '../../atomics/Skeletons/ExperienceSkeleton';
import SearchFilterModal from '../business/SearchFilterModal';
import FiltersModal from '../FiltersModal';
import { ExperienceUrlFilters } from '../FiltersModal/FiltersForm';
import AccountFilterResult, { User } from './AccountFilterResult';
import { businessDomainTabs } from './businessDomainTabs';
import { domainTabs } from './domainTabs';
import { mappedBusinesExperiences } from './helpers';
import * as SC from './styles';

interface Props {
  experiences?: Experience[];
  businessExperiences?: BusinessExperience[];
  filters: ExperienceUrlFilters;
  users?: User[];
  onShowExminderProfileClick?: (id: string) => void;
  onOpenFilters: (open: boolean) => void;
  onDomainFilter: CallbackFunction;
  onSelectSearchFilter: CallbackFunction;
  onUserFilterResultClick: CallbackFunction;
  requestIsLoading: boolean;
  isExminder: boolean;
  onCreateExperienceClick: CallbackFunction;
  onCreateAuctionClick: CallbackFunction;
  onTurnOnNotifications: CallbackFunction;
  isLoading: boolean;
  currentSearchFilter: SearchFilters;
  isTextSearched: boolean;
  isOnboardingSkipped: boolean;
  onBuildProfile: CallbackFunction;
  onSelectedCountry: CallbackFunction;
  onSearchedCity: CallbackFunction;
  onApplyFilters: CallbackFunction;
  onClearFilters: CallbackFunction;
  countries: Option[];
  cities: Option[];
  languages: Option[];
  searchText: string;
  searchFilter: string;
  activeDomainTab: string;
  currentAppMode: AppMode;
  handleAppModeToggleEnd: (value: string) => void;
}

export default function ExperienceFeedPageLayout(props: Props) {
  const {
    experiences,
    businessExperiences,
    filters,
    users,
    onShowExminderProfileClick,
    onOpenFilters,
    onDomainFilter,
    onSelectSearchFilter,
    onUserFilterResultClick,
    requestIsLoading,
    isExminder,
    onCreateAuctionClick,
    onCreateExperienceClick,
    onTurnOnNotifications,
    isLoading,
    currentSearchFilter,
    isTextSearched,
    isOnboardingSkipped,
    onBuildProfile,
    onSelectedCountry,
    onSearchedCity,
    onApplyFilters,
    onClearFilters,
    countries,
    cities,
    languages,
    searchText,
    searchFilter,
    activeDomainTab,
    currentAppMode,
    handleAppModeToggleEnd,
  } = props;
  const { t } = useTranslation();
  const [openFilters, setOpenFilters] = useState(false);

  useEffect(() => {
    onOpenFilters(openFilters);
  }, [openFilters]);

  const filtersCount = () => {
    let count = 0;

    count += filters.experienceType ? 1 : 0;
    count += filters.minPrice ? 1 : 0;
    count += filters.maxPrice ? 1 : 0;

    count += filters.currency ? 1 : 0;
    count += filters.country ? 1 : 0;
    count += filters.city ? 1 : 0;
    count += filters.language ? 1 : 0;
    count += filters.domain ? 1 : 0;
    count += filters.searchText ? 1 : 0;

    return count;
  };
  const mappedUserFilterResults = users?.map((user: User) => (
    <AccountFilterResult
      key={user.id}
      user={user}
      onAccountFilterResultClick={() => {
        onUserFilterResultClick(user.role, user.id, user.slug);
      }}
    />
  ));

  const normalizedExperiences = experiences?.map((experience: Experience) => (
    <ExperienceCard
      id={experience.id}
      key={experience.id}
      title={experience.title}
      presentationPhoto={experience.presentationPhoto}
      location={experience.city}
      domain={experience.domain}
      durationHours={experience.durationHours}
      durationMinutes={experience.durationMinutes}
      startDate={experience.startTime}
      endDate={experience.endTime}
      price={experience.price}
      currency={experience.currency}
      discount={experience.discountPercent}
      cardType={getExperienceCardType(experience)}
      typeOfExperience={experience.type}
      exminder={{
        id: experience.exminder?.id,
        firstName: experience.exminder?.firstName,
        lastName: experience.exminder?.lastName,
        profilePicture: experience.exminder?.profilePicture,
        workExperience: experience.exminder?.workExperience,
      }}
      actionButton={ActionButton.EXPLORE}
      href={experience.href}
      onShowExminderProfileClick={() =>
        onShowExminderProfileClick &&
        onShowExminderProfileClick(
          experience.slug?.contentCreatorFullName ?? ''
        )
      }
    />
  ));

  const renderExperiences = () => {
    const isBusinessMode = currentAppMode === AppMode.BUSINESS;
    const skeletonLoader = (
      <>
        <ExperienceSkeleton showUser hasActionButton />
        <ExperienceSkeleton showUser hasActionButton />
        <ExperienceSkeleton showUser hasActionButton />
      </>
    );

    if (
      !isLoading &&
      ((normalizedExperiences?.length === 0 && !isBusinessMode) ||
        (isBusinessMode &&
          mappedBusinesExperiences(businessExperiences ?? []).length === 0))
    ) {
      return (
        <EmptyPackagesState
          isExminder={isExminder}
          isBusinessMode={currentAppMode === AppMode.BUSINESS}
          onCreateAuctionClick={onCreateAuctionClick}
          onCreateExperienceClick={onCreateExperienceClick}
          onTurnOnNotifications={onTurnOnNotifications}
          isOnboardingSkipped={isOnboardingSkipped}
          onBuildProfile={onBuildProfile}
        />
      );
    }

    return (
      <>
        {currentAppMode === AppMode.INDIVIDUAL
          ? normalizedExperiences
          : mappedBusinesExperiences(businessExperiences ?? [])}
        {isLoading && skeletonLoader}
      </>
    );
  };
  const renderHeader = () => (
    <SC.FilteringContainer>
      <SC.SearchBarContainer>
        {ENABLE_APP_MODE_SWITCH ? (
          <AppModeSwitch
            defaultMode={currentAppMode}
            onModeChangeToggleEnd={handleAppModeToggleEnd}
          />
        ) : (
          <SearchBar
            placeholder={t(`experienceFeed.searchExperiencesPeople`)}
            onSearch={() => {}}
            value={searchText}
          />
        )}
      </SC.SearchBarContainer>
      <SC.FiltersButton>
        {filtersCount() > 0 && (
          <SC.FiltersCountBadge>{filtersCount()}</SC.FiltersCountBadge>
        )}
        <IconButton
          color={theme.palette.main.white}
          icon={<FiltersIcon />}
          iconSize={{ width: '24px', height: '24px' }}
          rotateIcon={90}
          onClick={() => {
            setOpenFilters(true);
          }}
        />
      </SC.FiltersButton>
    </SC.FilteringContainer>
  );

  const renderFiltersModal = () => {
    if (currentAppMode === AppMode.INDIVIDUAL) {
      return (
        <FiltersModal
          isOpen={openFilters}
          filters={filters}
          requestIsLoading={requestIsLoading}
          countries={countries}
          cities={cities}
          languages={languages}
          handleSelectedCountry={onSelectedCountry}
          handleSearchedCity={onSearchedCity}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          onClose={() => {
            setOpenFilters(false);
          }}
        />
      );
    }
    return (
      <SearchFilterModal
        isOpen={openFilters}
        filters={{
          domain: filters.domain,
          search: filters.searchText,
          city: '',
          country: '',
        }}
        requestIsLoading={requestIsLoading}
        countries={countries}
        cities={cities}
        handleSelectedCountry={onSelectedCountry}
        handleSearchedCity={onSearchedCity}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        onClose={() => {
          setOpenFilters(false);
        }}
        activeExperience={businessExperiences?.length ?? 0}
      />
    );
  };

  return (
    <SC.Container>
      <SC.Background
        className={menuBarClass}
        isBusiness={currentAppMode === AppMode.BUSINESS}
      >
        <SC.NavigationBarContainer className={backgroundContainerClass}>
          {renderHeader()}
          {filtersCount() > 0 ? null : (
            <SC.NavigationBarContent>
              <SC.TabBarContainer>
                <TabBar
                  tabs={
                    currentAppMode === AppMode.INDIVIDUAL
                      ? domainTabs()
                      : businessDomainTabs()
                  }
                  activeTab={activeDomainTab}
                  onTabFilter={onDomainFilter}
                />
              </SC.TabBarContainer>
            </SC.NavigationBarContent>
          )}
        </SC.NavigationBarContainer>
        {isTextSearched && currentAppMode === AppMode.INDIVIDUAL && (
          <SearchFiltersBar
            onSelectSearchFilter={onSelectSearchFilter}
            searchFilter={searchFilter}
          />
        )}
        <SC.MainContent>
          {currentSearchFilter === SearchFilters.EXPERIENCES && (
            <SC.ExperiencesContainer>
              {renderExperiences()}
            </SC.ExperiencesContainer>
          )}
          {currentSearchFilter === SearchFilters.ACCOUNTS && (
            <SC.UsersContainer>{mappedUserFilterResults}</SC.UsersContainer>
          )}
        </SC.MainContent>
      </SC.Background>
      {renderFiltersModal()}
    </SC.Container>
  );
}
